/**
 * CATIO READY™ — sales page content.
 *
 * Editorial rule: no invented testimonials, reviews, customer counts,
 * guarantees, certifications, savings, discounts or scarcity. Every number
 * here describes what the collection actually contains.
 */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface Volume {
  id: string
  number: string
  title: string
  projects: string
  short: string
  copy: string
  bullets: string[]
  cover: string
}

export interface ProjectPage extends PlanImage {
  volume: string
  page: string
  title: string
}

export interface TechProof extends PlanImage {
  label: string
  source: string
}

export interface Stat {
  value: string
  label: string
  accent?: string
}

export interface PlanStep {
  title: string
  hint: string
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export interface Faq {
  question: string
  answer: string
}

export const PRODUCT = {
  name: 'CATIO READY™',
  descriptor: 'The Complete Ready-to-Build Catio Plan System',
}

export const PRICE = {
  currency: 'US$',
  value: '27',
  amount: 'US$27',
  note: 'One-time payment · No subscription',
}

/** Hero CTA scrolls here. The real checkout lives inside this section. */
export const OFFER_ANCHOR = '#offer'

export const TOP_BAR_ITEMS: string[] = [
  '40 Ready-to-Build Projects',
  '5 Premium Volumes',
  'Imperial + Metric',
  'Instant Digital Access',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'The System', href: '#system' },
  { label: 'Volumes', href: '#collection' },
  { label: 'Projects', href: '#gallery' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

/** Transparent hero composition: 5 volumes, interior pages, bonus seal. */
export const HERO_MOCKUP: PlanImage = {
  src: 'https://cdn.magicpatterns.com/uploads/seJHjrEvwKtWnz3ZbZm9RC/nuevo_mockup_de_landing_sin_fondo.png',
  alt: 'The CATIO READY™ collection: all five volumes, a tablet showing a project plan, printed technical pages with detailed plans and a cut list, plus 5 practical bonuses included.',
  width: 1140,
  height: 1420,
}

export const VOLUMES: Volume[] = [
  {
    id: 'v01',
    number: '01',
    title: 'Window & Compact Catios',
    projects: '8 Complete Projects',
    short: 'Compact and window-access builds for smaller outdoor spaces.',
    copy: 'Ready-to-build designs for window access and compact outdoor spaces.',
    bullets: ['Window-access designs', 'Compact footprints', 'Approachable DIY builds'],
    cover: 'https://cdn.magicpatterns.com/uploads/dKxZ5AeUUkNxJWcjqDNnmv/V01_Cover_Clean.png',
  },
  {
    id: 'v02',
    number: '02',
    title: 'Balcony & Small-Space Catios',
    projects: '8 Complete Projects',
    short: 'Balcony-friendly and vertical builds for narrow, limited spaces.',
    copy: 'Projects designed for balconies, narrow patios and limited outdoor areas.',
    bullets: ['Balcony-friendly concepts', 'Narrow-space layouts', 'Vertical designs'],
    cover: 'https://cdn.magicpatterns.com/uploads/4oBYepopRMWWYU9vAvJ6nr/V02_Cover_Clean.png',
  },
  {
    id: 'v03',
    number: '03',
    title: 'Patio & Attached Catios',
    projects: '8 Complete Projects',
    short: 'House-side and patio builds for larger connected outdoor spaces.',
    copy: 'House-side and patio designs for larger connected outdoor spaces.',
    bullets: ['Attached layouts', 'Patio builds', 'Multiple size options'],
    cover: 'https://cdn.magicpatterns.com/uploads/rkRGSzu23FYnb7NxoWgdc3/V03_Cover_Clean.png',
  },
  {
    id: 'v04',
    number: '04',
    title: 'Freestanding Backyard Catios',
    projects: '8 Complete Projects',
    short: 'Standalone builds for gardens, patios and backyards.',
    copy: 'Standalone catio designs for gardens, patios and backyards.',
    bullets: ['Fully freestanding', 'Backyard placement', 'Multiple footprints'],
    cover: 'https://cdn.magicpatterns.com/uploads/d13GQnPohQhPK5MHGsjma9/V04_Cover_Clean.png',
  },
  {
    id: 'v05',
    number: '05',
    title: 'Walk-In & Large Catios',
    projects: '8 Complete Projects',
    short: 'Large walk-in builds with room for cats to climb, explore and relax.',
    copy: 'Large walk-in designs with room for cats to climb, explore and relax.',
    bullets: ['Human walk-in access', 'Large-format frames', 'Double-bay designs'],
    cover: 'https://cdn.magicpatterns.com/uploads/aCkBhJm6eLpHdQBTkWTqL7/V05_Cover_Clean.png',
  },
]

export const STATS: Stat[] = [
  { value: '5', label: 'Premium Volumes' },
  { value: '40', label: 'Ready-to-Build Projects' },
  { value: '200+', label: 'Pages' },
  { value: '5', label: 'Bonuses Included', accent: 'Free' },
]

const page = (
  src: string,
  volume: string,
  pageNumber: string,
  title: string,
): ProjectPage => ({
  src,
  volume,
  page: pageNumber,
  title,
  alt: `Real CATIO READY™ page: ${title} project opener from Volume ${volume}.`,
  width: 1530,
  height: 1980,
})

/** Real interior project pages for the proof carousel — Volume 01 → 05. */
export const PROJECT_PAGES: ProjectPage[] = [
  page('https://cdn.magicpatterns.com/uploads/14ZH3USPVtkJnaCCrvXa7N/01_V01_P29_Double_Window_Compact.png', '01', '29', 'Double-Window Compact Catio'),
  page('https://cdn.magicpatterns.com/uploads/cyi8P77BUzFa7KhsctMucQ/02_V02_P23_Sliding_Door_Sidecar.png', '02', '23', 'Sliding-Door Sidecar Catio'),
  page('https://cdn.magicpatterns.com/uploads/jpHsqyMMHXYHPmKUR47Hmh/03_V03_P29_Extended_Patio_Wall.png', '03', '29', 'Extended Patio Wall Catio'),
  page('https://cdn.magicpatterns.com/uploads/kUCatNeUxycS5UJXuwNtUT/04_V04_P29_Backyard_Panorama.png', '04', '29', 'Backyard Panorama Catio'),
  page('https://cdn.magicpatterns.com/uploads/1eEVoH1QBfZugZxa3Ed765/05_V05_P23_Wide_Panorama_WalkIn.png', '05', '23', 'Wide Panorama Walk-In Catio'),
  page('https://cdn.magicpatterns.com/uploads/gtATmdJC1L5Tzzdgyr5Trp/06_V05_P29_Grand_Garden_Room.png', '05', '29', 'Grand Garden Room Catio'),
]

const [p01, , p03, p04, p05, p06] = PROJECT_PAGES

/** 15 real project previews, 3 per volume, smallest → largest. */
export const GALLERY: ProjectPage[] = [
  page('https://cdn.magicpatterns.com/uploads/pKshYSBNVa62RELGGMdN6t/01_V01_P08_Simple_Window_Box.png', '01', '08', 'Simple Window Box Catio'),
  page('https://cdn.magicpatterns.com/uploads/x5RfubcVfJmAB5QvNddo8S/02_V01_P17_Tall_Window_Tower.png', '01', '17', 'Tall Window Tower Catio'),
  p01,
  page('https://cdn.magicpatterns.com/uploads/nQUEjTjMV8RJnuBFWMhgqy/04_V02_P08_Micro_Balcony_Rail.png', '02', '08', 'Micro Balcony Rail Catio'),
  page('https://cdn.magicpatterns.com/uploads/npKVF2xYekZmWEPpj83MH4/05_V02_P14_Balcony_Corner_Tower.png', '02', '14', 'Balcony Corner Tower Catio'),
  page('https://cdn.magicpatterns.com/uploads/tgCoFVyPkZMbFnsdynkdTq/06_V02_P29_Double_Bay_Balcony_Lounge.png', '02', '29', 'Double-Bay Balcony Lounge Catio'),
  page('https://cdn.magicpatterns.com/uploads/mS1mVzhEfQ5v9FU2B8pkRb/07_V03_P08_Low_Profile_Patio_Wall.png', '03', '08', 'Low-Profile Patio Wall Catio'),
  page('https://cdn.magicpatterns.com/uploads/hjvqohUbw8rCopaXqFxRbU/08_V03_P20_Tall_Attached_Climbing.png', '03', '20', 'Tall Attached Climbing Catio'),
  p03,
  page('https://cdn.magicpatterns.com/uploads/iKH824ooZWH6m773qLr4oS/10_V04_P14_Freestanding_Climbing_Tower.png', '04', '14', 'Freestanding Climbing Tower Catio'),
  page('https://cdn.magicpatterns.com/uploads/whorHzocM9858ehXpaoRs6/11_V04_P23_Double_Bay_Garden.png', '04', '23', 'Double-Bay Garden Catio'),
  p04,
  page('https://cdn.magicpatterns.com/uploads/kWDojCHdzJZXoWCcGSy1ck/13_V05_P14_Wide_WalkIn_Lounge.png', '05', '14', 'Wide Walk-In Lounge Catio'),
  p05,
  p06,
]

export const WITHOUT_PLAN =
  'Guess the dimensions, guess the materials, guess every cut — then solve problems while building and hope it all fits.'

export const PLAN_STEPS: PlanStep[] = [
  { title: 'Choose', hint: 'Pick one of 40 projects' },
  { title: 'Buy', hint: 'Follow the materials list' },
  { title: 'Cut', hint: 'Work from the cut list' },
  { title: 'Assemble', hint: 'Follow the technical views, step by step' },
  { title: 'Inspect', hint: 'Run the final check — then enjoy' },
]

export const PROJECT_INCLUDES: string[] = [
  'Materials',
  'Cut Lists',
  'Finished Dimensions',
  'Technical Views',
  'Component Labels',
  'Assembly Sequence',
  'Inspection Guidance',
  'Imperial + Metric',
]

/** Real technical crops from the plans. */
export const TECH_PROOF: TechProof[] = [
  {
    label: 'Know the Measurements',
    source: 'Volume 01 · Project 01 — Technical plan',
    src: 'https://cdn.magicpatterns.com/uploads/hqx6KTCiVg9iqzzoGDzND7/01_V01_P09_Orthographic_Views.png',
    alt: 'Front, left side and top views of a compact catio with 36 in and 24 in dimensions and labeled components A, B, C, D, M1 and M4.',
    width: 1460,
    height: 785,
  },
  {
    label: 'Know What to Cut',
    source: 'Volume 01 · Project 01 — Cut list + mesh cut list',
    src: 'https://cdn.magicpatterns.com/uploads/mD8WWL6aSuP4yTFsDUQEgx/02_V01_P09_Cut_Lists.png',
    alt: 'Cut list and raw mesh cut list tables with part, quantity, and imperial and metric cut sizes.',
    width: 1440,
    height: 575,
  },
  {
    label: 'Know How It Fits Together',
    source: 'Volume 02 · Project 04 — Exploded technical view',
    src: 'https://cdn.magicpatterns.com/uploads/dY9avA3PufCaRDS281erKh/04_V02_P18_Exploded_Technical_View.png',
    alt: 'Exploded technical view of a catio frame showing labeled posts, rails, floor, door and mesh panels with imperial and metric dimensions.',
    width: 970,
    height: 1560,
  },
  {
    label: 'Know How to Assemble It',
    source: 'Volume 01 · Project 01 — Build sequence',
    src: 'https://cdn.magicpatterns.com/uploads/q91EvsZP6S5MtqRtkorYPt/03_V01_P10_Build_Sequence.png',
    alt: 'Eight-step build sequence from verifying materials and cutting parts to assembling the frame, installing floor and mesh, and finishing.',
    width: 1090,
    height: 1645,
  },
]

const bonusImage = (src: string, title: string) => ({
  src,
  alt: `CATIO READY™ bonus cover: ${title}.`,
  width: 1122,
  height: 1402,
})

export const BONUSES: Bonus[] = [
  {
    number: '01',
    title: 'Placement & Measurement Planner',
    description:
      'Plan the location, record key measurements and compare your available space before building.',
    ...bonusImage('https://cdn.magicpatterns.com/uploads/eYbtHU41jtDysZC3pTGra8/Bono_Portada_1.png', 'Placement & Measurement Planner'),
  },
  {
    number: '02',
    title: 'Safety & Escape-Prevention Checklist',
    description:
      'Use a practical inspection routine before first use and during ongoing maintenance.',
    ...bonusImage('https://cdn.magicpatterns.com/uploads/5s9B1PL7gZCbPymBiU4KEr/Bono_Portada_2.png', 'Safety & Escape-Prevention Checklist'),
  },
  {
    number: '03',
    title: 'Materials & Budget Planner',
    description:
      'Organize materials, compare suppliers and keep your project spending under control.',
    ...bonusImage('https://cdn.magicpatterns.com/uploads/gse6UDBL8V8M2drc6PR4A7/Bono_Portada_3.png', 'Materials & Budget Planner'),
  },
  {
    number: '04',
    title: 'Cat Enrichment & Catio Setup Guide',
    description:
      'Turn the finished enclosure into a more comfortable and engaging space for your cat.',
    ...bonusImage('https://cdn.magicpatterns.com/uploads/jrCxM2jazXJs9ucZyHTNU3/Bono_Portada_4.png', 'Cat Enrichment & Catio Setup Guide'),
  },
  {
    number: '05',
    title: 'Build-Day & Maintenance Checklist',
    description:
      'Stay organized during construction and keep your catio maintained afterward.',
    ...bonusImage('https://cdn.magicpatterns.com/uploads/3aMz7mSS9YMSCP8g3akBVu/Bono_Portada_5.png', 'Build-Day & Maintenance Checklist'),
  },
]

export const BONUS_GROUP: PlanImage = {
  src: 'https://cdn.magicpatterns.com/uploads/aTmTGb86gKi1idqU1oGCTC/5_bonos_mockup.png',
  alt: 'All five CATIO READY™ bonus covers together: Placement & Measurement Planner, Safety & Escape-Prevention Checklist, Materials & Budget Planner, Cat Enrichment & Catio Setup Guide, and Build-Day & Maintenance Checklist.',
  width: 1448,
  height: 1086,
}

export const OFFER_ITEMS: string[] = [
  '5 Premium Volumes',
  '40 Ready-to-Build Projects',
  '200+ Pages',
  '5 Bonuses Included FREE',
  'Imperial + Metric',
  'Instant Digital Access',
]

export const GUESSWORK: string[] = [
  'Wrong materials',
  'Incorrect cuts',
  'Missing mesh',
  'Rebuilding a door',
  'Extra hardware-store trips',
  'Designing while building',
]

export const ACCESS_STEPS: string[] = [
  'Complete secure checkout',
  'Get instant digital access',
  'Download your CATIO READY™ files',
  'Choose your first build',
]

export const FAQS: Faq[] = [
  {
    question: 'Is CATIO READY™ a physical product?',
    answer: 'No. It is a digital collection of downloadable PDF plans.',
  },
  {
    question: 'Do I need professional woodworking experience?',
    answer:
      'No professional experience is required, but you should be comfortable using basic DIY tools safely.',
  },
  {
    question: 'Are measurements Imperial or Metric?',
    answer: 'Both systems are included.',
  },
  {
    question: 'What does each project include?',
    answer:
      'Materials, cut lists, dimensions, technical drawings, assembly guidance and inspection information.',
  },
  {
    question: 'Can I give the plans to a carpenter or handyman?',
    answer: 'Yes.',
  },
  {
    question: 'Can I build finished catios and sell them?',
    answer:
      'Finished physical builds may be sold. The CATIO READY™ digital plans themselves may not be redistributed or resold.',
  },
  {
    question: 'Can I modify a plan to fit my space?',
    answer:
      'Real site conditions vary. Measure your actual location and verify adaptations before building.',
  },
  {
    question: 'Are these plans structurally certified for every location?',
    answer:
      'No. Local conditions, weather exposure, attachment requirements and regulations must be verified where applicable.',
  },
  {
    question: 'How do I receive CATIO READY™?',
    answer: 'Through digital access/download after checkout.',
  },
]

export const DISCLAIMER: string[] = [
  'CATIO READY™ is a digital DIY plan collection.',
  'Actual site conditions, materials, local codes, structural requirements, anchoring needs and weather exposure vary.',
  'Users are responsible for verifying local conditions and using appropriate tools and safe working practices.',
  'CATIO READY™ does not claim universal structural certification, load ratings, predator-proof performance or escape-proof performance.',
  'Digital files may not be redistributed or resold.',
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'
